import * as Yup from 'yup'
import {
  DEFAULT_FILE_LIMIT_IN_MB,
  USERNAME_REG_EXP,
  NAME_REG_EXP,
  TEAM_REG_EXP,
  EMAIL_SIZE_LIMIT,
  EMAIL_REG_EXP_V2,
  PHONE_REG_EXP,
  EMAIL_OR_PHONE_REG_EXP,
} from 'common/constants'
import {
  EMAIL_SIZE_VALIDATION,
  EMAIL_OR_PHONE_REG_EXP_VALIDATION,
  DUPLICATE_EMAIL,
  EMAIL_REG_EXP_VALIDATION,
  NAME_REG_EXP_VALIDATION,
  TEAM_REG_EXP_VALIDATION,
  USERNAME_REQUIRED,
  USERNAME_MIN_LENGTH_REQUIRED,
  USERNAME_MAX_LENGTH__REQUIRED,
  USERNAME_REG_EXP_VALIDATION,
  FULL_NAME_REQUIRED,
  FULL_NAME_MIN_LENGTH_REQUIRED,
  FULL_NAME_MAX_LENGTH__REQUIRED,
  FULL_NAME_REG_EXP_VALIDATION,
  VERIFICATION_CODE_REQUIRED,
  VALID_CODE_REQUIRED,
  DISPLAY_NAME_REQUIRED,
  DISPLAY_NAME_MIN_LENGTH_REQUIRED,
  DISPLAY_NAME_MAX_LENGTH__REQUIRED,
  TEAM_NAME_REQUIRED,
  TEAM_NAME_MIN_LENGTH_REQUIRED,
  TEAM_NAME_MAX_LENGTH__REQUIRED,
  EMAIL_REQUIRED,
  INVITE_EMAIL_REQUIRED,
  ONE_INVITEE_EMAIL_REQUIRED,
  STREET_ADDRESS_REQUIRED,
  COUNTRY_REQUIRED,
  CITY_REQUIRED,
  STATE_REQUIRED,
  ZIP_CODE_REQUIRED,
  FULL_NAME_CARD_REQUIRED,
  TERMS_CHECKED_REQUIRED,
  ADMIN_ROLE_REQUIRED,
  CONFIRMATION_TEXT_REQUIRED,
  VALID_CONFIRMATION_TEXT_REQUIRED,
} from 'common/messages'

Yup.addMethod(Yup.array, 'unique', function(message, mapper = a => a) {
  return this.test('unique', message, function(list) {
    return list.length === new Set(list.map(mapper)).size
  })
})

export const validations = {
  name: Yup.string()
    .trim()
    .min(3, DISPLAY_NAME_MIN_LENGTH_REQUIRED)
    .max(30, DISPLAY_NAME_MAX_LENGTH__REQUIRED)
    .matches(NAME_REG_EXP, NAME_REG_EXP_VALIDATION)
    .required(DISPLAY_NAME_REQUIRED),
  teamName: Yup.string()
    .trim()
    .min(3, TEAM_NAME_MIN_LENGTH_REQUIRED)
    .max(30, TEAM_NAME_MAX_LENGTH__REQUIRED)
    .matches(TEAM_REG_EXP, TEAM_REG_EXP_VALIDATION)
    .required(TEAM_NAME_REQUIRED),
  vatId: Yup.string()
    .trim()
    .max(12)
    .notRequired(),
  email: Yup.string()
    .matches(EMAIL_REG_EXP_V2, EMAIL_REG_EXP_VALIDATION)
    .max(EMAIL_SIZE_LIMIT, EMAIL_SIZE_VALIDATION)
    .required(EMAIL_REQUIRED),
  phone: Yup.string().matches(PHONE_REG_EXP, EMAIL_OR_PHONE_REG_EXP_VALIDATION),
  emailOrPhone: Yup.string().matches(
    EMAIL_OR_PHONE_REG_EXP,
    EMAIL_OR_PHONE_REG_EXP_VALIDATION
  ),
  address1: Yup.string()
    .trim()
    .required(STREET_ADDRESS_REQUIRED),
  address2: Yup.string()
    .trim()
    .notRequired(),
  country: Yup.string()
    .trim()
    .required(COUNTRY_REQUIRED),
  city: Yup.string()
    .trim()
    .required(CITY_REQUIRED),
  state: Yup.string()
    .trim()
    .required(STATE_REQUIRED),
  zipCode: Yup.string()
    .trim()
    .required(ZIP_CODE_REQUIRED),
  couponCode: Yup.string()
    .trim()
    .notRequired(),
  fullNameOnCard: Yup.string()
    .trim()
    .required(FULL_NAME_CARD_REQUIRED),
  teamLogo: Yup.mixed(),
  deleteAccount: Yup.string()
    .oneOf(['delete'], VALID_CONFIRMATION_TEXT_REQUIRED)
    .required(CONFIRMATION_TEXT_REQUIRED),
  cancelPlan: Yup.string()
    .oneOf(['cancel'], VALID_CONFIRMATION_TEXT_REQUIRED)
    .required(CONFIRMATION_TEXT_REQUIRED),
  downgradeAccount: Yup.string()
    .oneOf(['downgrade'], VALID_CONFIRMATION_TEXT_REQUIRED)
    .required(CONFIRMATION_TEXT_REQUIRED),
  username: Yup.string()
    .min(2, USERNAME_MIN_LENGTH_REQUIRED)
    .max(30, USERNAME_MAX_LENGTH__REQUIRED)
    .matches(USERNAME_REG_EXP, USERNAME_REG_EXP_VALIDATION)
    .required(USERNAME_REQUIRED),
  realName: Yup.string()
    .trim()
    .min(3, FULL_NAME_MIN_LENGTH_REQUIRED)
    .max(30, FULL_NAME_MAX_LENGTH__REQUIRED)
    .matches(NAME_REG_EXP, FULL_NAME_REG_EXP_VALIDATION)
    .required(FULL_NAME_REQUIRED),
  loginCode: Yup.string()
    .min(6, VALID_CODE_REQUIRED)
    .required(VERIFICATION_CODE_REQUIRED),
  invities: Yup.array()
    .of(
      Yup.lazy(value => {
        if (value) {
          return Yup.string()
            .matches(EMAIL_REG_EXP_V2, EMAIL_REG_EXP_VALIDATION)
            .max(EMAIL_SIZE_LIMIT, EMAIL_SIZE_VALIDATION)
            .required(INVITE_EMAIL_REQUIRED)
        }
        return Yup.string().notRequired()
      })
    )
    .unique(DUPLICATE_EMAIL, a => a)
    .required(),
  teamSetupInvities: Yup.array()
    .of(
      Yup.lazy(value => {
        if (value) {
          return Yup.string()
            .matches(EMAIL_REG_EXP_V2, EMAIL_REG_EXP_VALIDATION)
            .max(EMAIL_SIZE_LIMIT, EMAIL_SIZE_VALIDATION)
            .required(INVITE_EMAIL_REQUIRED)
        }
        return Yup.string().notRequired()
      })
    )
    .unique(DUPLICATE_EMAIL, a => a)
    .notRequired(),
  isTermsChecked: Yup.boolean()
    .oneOf([true], TERMS_CHECKED_REQUIRED)
    .required(TERMS_CHECKED_REQUIRED),
  isRole: Yup.boolean()
    .oneOf([true], ADMIN_ROLE_REQUIRED)
    .required(ADMIN_ROLE_REQUIRED),
}

export const SUPPORTED_FORMATS = {
  image: ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'],
}

export const isValidFormat = ({ file, formats }) => {
  return file && formats.includes(file.type)
}

export const isValidImage = ({ file }) => {
  return isValidFormat({ file, formats: SUPPORTED_FORMATS.image })
}

export const isValidImageSize = fileSize => {
  return fileSize / Math.pow(1024, 2) <= DEFAULT_FILE_LIMIT_IN_MB ? true : false
}
